import type { Level, LogFn } from "pino";

export interface LogContext {
  exception?: Error;
  [key: string]: unknown;
}

export type LogEntry =
  | { kind: "contextual"; context: LogContext; message: string }
  | { kind: "plain"; message: string; exception?: Error };

export interface StoredLog {
  level: Level;
  log: LogEntry;
  timestamp: number;
  timestampHumanReadable: string;
}

// map each event "type" to its specific payload
export interface WideEventKindMap {
  httpRequest: HTTPRequestLogInfo;
  trpcProcedure: TRPCProcedureLogInfo;
}

// generic WideEvent, defaulting to the union of all kinds
export type WideEvent<K extends keyof WideEventKindMap = keyof WideEventKindMap> = {
  correlationId: string;
  frontendAppVersion: "unknown" | "admin" | (string & {});
  logs: StoredLog[];
  type: K;
  durationMs: number;
} & WideEventKindMap[K];

export const isWideEventLog = (log: unknown): log is WideEvent => {
  return typeof log === "object" && log !== null && "correlationId" in log && "logs" in log && Array.isArray(log.logs);
};

export const isHttpWideEvent = (log: WideEvent): log is WideEvent<"httpRequest"> => {
  return log.type === "httpRequest";
};

export const isTRPCWideEvent = (log: WideEvent): log is WideEvent<"trpcProcedure"> => {
  return log.type === "trpcProcedure";
};

// Create payload without logs, still generic over the kind
export type CreateWideEventPayload<K extends keyof WideEventKindMap> = Omit<WideEvent<K>, "logs">;

interface HTTPRequestLogInfo {
  httpMethod: string;
  httpStatus: number;
  httpUrl: string;
  tenant?: string;
  // On some api routes we don't have a user id (e.g. health check/auth)
  userId?: string;
}

interface TRPCProcedureLogInfo {
  trpcProcedurePath: string;
  userId: string;
  organisationId?: string;
  projectId?: string;
  trpcProcedureType: "query" | "mutation" | "subscription";
  trpcProcedureStatus: number;
}

export interface LoggerInterface {
  trace: LogFn;
  debug: LogFn;
  info: LogFn;
  warn: LogFn;
  error: LogFn;
  fatal: LogFn;
}

export const HEADER_FRONTEND_APP_VERSION = "X-Frontend-App-Version";
export const HEADER_PINNED_SESSION_ID = "X-Pinned-Session-Id";
